import React from "react";
import { Reveal, Stagger, StaggerItem } from "../../lib/motion";
import { useApp } from "@/context/AppContext";
import useOrangeCareCatalog, { gbpMajor } from "@/hooks/useOrangeCareCatalog";

const TARGET_BY_KEY = {
  lp:  "landing_page",
  web: "custom_website",
  crm: "crm_business_system",
};

export default function OrangeCare() {
  const { t, currency } = useApp();
  const tiers = t("care.tiers") || [];
  const { byKey, status } = useOrangeCareCatalog();

  const catalogByTarget = Object.values(byKey).reduce((acc, p) => {
    if (p.care_target) acc[p.care_target] = p;
    return acc;
  }, {});

  const CUR_SYM = { GBP: "£", USD: "$", EUR: "€", AED: "AED " };
  const fmtMinor = (minor) => {
    if (minor == null || !Number.isFinite(Number(minor))) return null;
    const major = Number(minor) / 100;
    const sym = CUR_SYM[currency] || (currency + " ");
    // AED historically displayed without decimals in the canonical
    // pricing table (110, 1100, 200, 2000). GBP/USD/EUR use 2dp.
    if (currency === "AED") return `${sym}${Math.round(major)}`;
    return `${sym}${major.toFixed(2)}`;
  };

  return (
    <section id="care" data-testid="care-section" className="relative py-20 md:py-28">
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-10">
        <Reveal>
          <p className="text-[11px] tracking-[0.24em] uppercase text-black/75 mb-4 font-bold">
            {t("care.eyebrow")}
          </p>
          <h2 className="font-display font-black text-black leading-[1.02] tracking-tight text-4xl md:text-5xl lg:text-6xl">
            {t("care.title1")}<br />
            <span className="text-black/55">{t("care.title2")}</span>
          </h2>
          <p className="mt-5 max-w-2xl text-black/80 text-base md:text-lg leading-relaxed">
            {t("care.sub")}
          </p>
        </Reveal>
        <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((tier) => {
            const plan = catalogByTarget[TARGET_BY_KEY[tier.key]];
            const isPoa = plan?.pricing_mode === "poa";
            const curPrices = (plan?.prices || {})[currency] || {};
            const monthLabel = isPoa ? "POA" : fmtMinor(curPrices.monthly);
            const yearLabel  = isPoa ? null  : fmtMinor(curPrices.annual);
            const noData = status === "error"
                        || (status === "ready" && !plan)
                        || (status === "ready" && !isPoa && monthLabel == null);

            return (
              <StaggerItem key={tier.key}>
                <div
                  data-testid={`care-tier-${tier.key}`}
                  className="h-full rounded-3xl p-8 bg-white border border-black/10 hover:border-black/30 hover:-translate-y-0.5 transition-all shadow-[0_20px_50px_-25px_rgba(0,0,0,0.25)]"
                >
                  <div className="text-[11px] tracking-[0.22em] uppercase text-black/60 font-bold">OrangeCare</div>
                  <h3 className="mt-2 font-display font-black text-2xl text-black tracking-tight">{tier.name}</h3>

                  {noData ? (
                    <div className="mt-5">
                      <p className="text-[15px] text-black/70">Pricing temporarily unavailable.</p>
                      <a href="#discovery" className="mt-3 inline-flex text-[13px] font-bold underline text-black">Contact us</a>
                    </div>
                  ) : status === "loading" ? (
                    <div className="mt-5 h-16 rounded-lg bg-black/5 animate-pulse"/>
                  ) : isPoa ? (
                    <>
                      <div
                        className="mt-5 flex items-baseline gap-2 flex-wrap"
                        data-testid={`care-price-${tier.key}`}
                      >
                        <span className="font-display font-black text-4xl text-black tracking-tight" data-testid={`care-price-${tier.key}-poa`}>POA</span>
                      </div>
                      <p className="mt-2 text-[13px] text-black/70">
                        Priced per your CRM/business-system requirements.
                      </p>
                    </>
                  ) : (
                    <>
                      <div
                        className="mt-5 flex items-baseline gap-2 flex-wrap"
                        style={{ fontVariantNumeric: "tabular-nums" }}
                        data-testid={`care-price-${tier.key}`}
                      >
                        <span
                          className="font-display font-black text-4xl text-black tracking-tight"
                          data-testid={`care-price-${tier.key}-${currency}-monthly`}
                        >
                          {monthLabel}
                        </span>
                        <span className="text-black/70 text-sm font-semibold">{t("services.per_month")}</span>
                      </div>
                      {yearLabel && (
                        <p
                          className="mt-2 text-[13px] text-black/70"
                          data-testid={`care-price-${tier.key}-${currency}-annual`}
                        >
                          {t("services.or")}{" "}
                          <span className="font-bold text-black">{yearLabel}</span>
                          {t("services.per_year")}
                        </p>
                      )}
                    </>
                  )}
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
        <Reveal>
          <p data-testid="care-note" className="mt-8 max-w-4xl text-black/70 text-[13px] leading-relaxed">
            {t("care.note")}
          </p>
        </Reveal>
      </div>
    </section>
  );
}
